/**
 * articleHtml — pure helpers over a content piece's canonical `html`.
 *
 * The article body is a flat list of top-level blocks. Image slots live in
 * that body as `<figure data-image-slot="<key>" data-status="queued">`
 * markers; once an option is chosen the marker becomes
 * `<figure data-image="<key>"><img src alt></figure>` so the approval agent
 * sees no leftover `data-image-slot` attributes but the slot can still be
 * re-found if a different option is picked later.
 *
 * Everything here is DOM-only (DOMParser) and side-effect free.
 */

export const SLOT_ATTR = 'data-image-slot';
export const PLACED_ATTR = 'data-image';

export type ArticleBlock =
  | {
      kind: 'html';
      /** Index among content blocks (slots excluded) — the anchor `after_block`. */
      index: number;
      html: string;
      text: string;
      tag: string;
    }
  | {
      kind: 'slot';
      slotKey: string;
      status: string;
      /** true once an image has been placed for this slot */
      placed: boolean;
      html: string;
    };

export type SlotAnchor = { after_block: number; after_text: string };

function parse(html: string): { doc: Document; body: HTMLElement } {
  const doc = new DOMParser().parseFromString(`<!doctype html><html><body>${html}</body></html>`, 'text/html');
  return { doc, body: doc.body };
}

function normaliseText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function isSlotFigure(el: Element): boolean {
  return el.tagName === 'FIGURE' && (el.hasAttribute(SLOT_ATTR) || el.hasAttribute(PLACED_ATTR));
}

function slotKeyOf(el: Element): string {
  return el.getAttribute(SLOT_ATTR) || el.getAttribute(PLACED_ATTR) || '';
}

/** Split `html` into top-level blocks. Loose text nodes get wrapped in a paragraph. */
export function parseArticle(html: string): ArticleBlock[] {
  const { doc, body } = parse(html);
  const blocks: ArticleBlock[] = [];
  let index = 0;
  Array.from(body.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = normaliseText(node.textContent || '');
      if (!text) return;
      const p = doc.createElement('p');
      p.textContent = text;
      blocks.push({ kind: 'html', index: index++, html: p.outerHTML, text, tag: 'p' });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (isSlotFigure(el)) {
      blocks.push({
        kind: 'slot',
        slotKey: slotKeyOf(el),
        status: el.getAttribute('data-status') || (el.hasAttribute(PLACED_ATTR) ? 'selected' : 'queued'),
        placed: el.hasAttribute(PLACED_ATTR) || !!el.querySelector('img'),
        html: el.outerHTML,
      });
      return;
    }
    blocks.push({
      kind: 'html',
      index: index++,
      html: el.outerHTML,
      text: normaliseText(el.textContent || ''),
      tag: el.tagName.toLowerCase(),
    });
  });
  return blocks;
}

/** Plain text of the whole article, whitespace collapsed. */
export function articleText(html: string): string {
  return normaliseText(parse(html).body.textContent || '');
}

/** First `<h1>` text, if the writer put the title in the body. */
export function articleHeading(html: string): string | null {
  const h1 = parse(html).body.querySelector('h1');
  const text = h1 ? normaliseText(h1.textContent || '') : '';
  return text || null;
}

function findSlot(body: HTMLElement, slotKey: string): Element | null {
  const sel = (attr: string) => `figure[${attr}="${CSS.escape(slotKey)}"]`;
  return body.querySelector(sel(SLOT_ATTR)) || body.querySelector(sel(PLACED_ATTR));
}

function mutate(html: string, fn: (body: HTMLElement, doc: Document) => void): string {
  const { doc, body } = parse(html);
  fn(body, doc);
  return body.innerHTML;
}

function nthContentBlock(body: HTMLElement, index: number): Element | null {
  let i = 0;
  for (const el of Array.from(body.children)) {
    if (isSlotFigure(el)) continue;
    if (i === index) return el;
    i += 1;
  }
  return null;
}

/**
 * Insert a queued slot marker after content block `afterBlock`
 * (-1 = before the first block). Any slot markers already sitting right
 * after that block are skipped so the new one lands last in the run.
 */
export function insertSlotMarker(html: string, afterBlock: number, slotKey: string): string {
  return mutate(html, (body, doc) => {
    const figure = doc.createElement('figure');
    figure.setAttribute(SLOT_ATTR, slotKey);
    figure.setAttribute('data-status', 'queued');
    if (afterBlock < 0) {
      body.insertBefore(figure, body.firstChild);
      return;
    }
    const anchor = nthContentBlock(body, afterBlock);
    if (!anchor) {
      body.appendChild(figure);
      return;
    }
    let cursor: Element = anchor;
    while (cursor.nextElementSibling && isSlotFigure(cursor.nextElementSibling)) {
      cursor = cursor.nextElementSibling;
    }
    cursor.insertAdjacentElement('afterend', figure);
  });
}

/** Drop a slot (marker or placed figure) from the body. */
export function removeSlotMarker(html: string, slotKey: string): string {
  return mutate(html, (body) => {
    findSlot(body, slotKey)?.remove();
  });
}

/** Update `data-status` on a still-unplaced marker. No-op for placed figures. */
export function markSlotStatus(html: string, slotKey: string, status: string): string {
  return mutate(html, (body) => {
    const el = findSlot(body, slotKey);
    if (el && el.hasAttribute(SLOT_ATTR)) el.setAttribute('data-status', status);
  });
}

/** Swap the marker (or a previously placed figure) for the chosen image. */
export function placeSlotImage(html: string, slotKey: string, url: string, alt: string): string {
  return mutate(html, (body, doc) => {
    const existing = findSlot(body, slotKey);
    const figure = doc.createElement('figure');
    figure.setAttribute(PLACED_ATTR, slotKey);
    const img = doc.createElement('img');
    img.setAttribute('src', url);
    img.setAttribute('alt', alt);
    img.setAttribute('loading', 'lazy');
    figure.appendChild(img);
    if (existing) existing.replaceWith(figure);
    else body.appendChild(figure);
  });
}

/** Does the body still carry unplaced markers? */
export function hasPendingSlots(html: string): boolean {
  return parse(html).body.querySelector(`figure[${SLOT_ATTR}]`) !== null;
}

/** Anchor for a slot inserted after content block `afterBlock`. */
export function slotAnchor(blocks: ArticleBlock[], afterBlock: number): SlotAnchor {
  const block = blocks.find((b): b is Extract<ArticleBlock, { kind: 'html' }> => b.kind === 'html' && b.index === afterBlock);
  return { after_block: afterBlock, after_text: (block?.text || '').slice(0, 80) };
}

/** Copy the prompt is written from: the block above the slot plus the one above that, capped. */
export function slotContext(blocks: ArticleBlock[], afterBlock: number, cap = 600): string {
  const content = blocks.filter((b): b is Extract<ArticleBlock, { kind: 'html' }> => b.kind === 'html');
  const above = content.filter((b) => b.index <= afterBlock && b.text).slice(-2);
  const text = above.map((b) => b.text).join('\n\n');
  return text.length > cap ? `${text.slice(0, cap).trimEnd()}…` : text;
}

/** Featured image context: the title plus the opening of the article. */
export function featuredContext(title: string, html: string, cap = 1200): string {
  const body = articleText(html);
  const opening = body.length > cap ? `${body.slice(0, cap).trimEnd()}…` : body;
  return [title.trim(), opening].filter(Boolean).join('\n\n');
}

export function slugify(title: string): string {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || 'article';
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const STANDALONE_CSS = `
  :root { color-scheme: light dark; }
  body { margin: 0; font-family: system-ui, -apple-system, "Segoe UI", sans-serif; line-height: 1.65; color: #1b1a1c; background: #fff; }
  @media (prefers-color-scheme: dark) { body { color: #f5f2ef; background: #151415; } }
  article { max-width: 720px; margin: 0 auto; padding: 48px 20px 96px; font-size: 17px; }
  h1 { font-size: 2.1em; line-height: 1.15; letter-spacing: -0.02em; margin: 0 0 0.6em; }
  h2 { font-size: 1.5em; line-height: 1.25; margin: 1.6em 0 0.5em; }
  h3 { font-size: 1.2em; margin: 1.4em 0 0.4em; }
  p, ul, ol, blockquote, pre { margin: 0 0 1.1em; }
  figure { margin: 1.8em 0; }
  figure img, img { display: block; width: 100%; height: auto; border-radius: 10px; }
  figcaption { font-size: 0.85em; opacity: 0.7; margin-top: 0.5em; }
  blockquote { border-left: 3px solid rgba(127,127,127,0.4); padding-left: 1em; opacity: 0.9; }
  code, pre { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 0.9em; }
  pre { padding: 1em; border-radius: 8px; background: rgba(127,127,127,0.12); overflow-x: auto; }
  a { color: #c04a2d; }
`;

export type StandaloneInput = {
  title: string;
  html: string;
  featured?: { url: string; alt: string } | null;
};

/** Wrap the body in a minimal standalone document: doctype, title, inline stylesheet, featured figure first. */
export function standaloneDocument({ title, html, featured }: StandaloneInput): string {
  const safeTitle = escapeHtml(title.trim() || 'Untitled');
  const heading = articleHeading(html);
  const featuredFigure = featured
    ? `<figure class="featured"><img src="${escapeHtml(featured.url)}" alt="${escapeHtml(featured.alt)}"></figure>\n`
    : '';
  const titleBlock = heading ? '' : `<h1>${safeTitle}</h1>\n`;
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${safeTitle}</title>`,
    `<style>${STANDALONE_CSS}</style>`,
    '</head>',
    '<body>',
    '<article>',
    `${featuredFigure}${titleBlock}${html.trim()}`,
    '</article>',
    '</body>',
    '</html>',
    '',
  ].join('\n');
}
